import { API_URLS } from './urls';
import { getToken } from './auth';

// ─── Shared helper ────────────────────────────────────────────────────────────

async function apiFetch<T>(url: string, init: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      (data as { detail?: string; message?: string }).detail ??
      (data as { detail?: string; message?: string }).message ??
      `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return data as T;
}

// ─── Teacher attendance types ─────────────────────────────────────────────────

export interface TeacherAttendanceEntry {
  id: number;
  teacher_id: number;
  date: string;
  status: 'present' | 'absent' | 'late';
  note: string | null;
  marked_by: number;
  updated_by: number | null;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
}

export interface MarkTeacherAttendanceRequest {
  teacher_id: number;
  date: string;
  status: 'present' | 'absent' | 'late';
  note?: string | null;
}

export interface MarkTeacherAttendanceResponse {
  message: string;
  data: TeacherAttendanceEntry;
}

// ─── Class / student attendance types ────────────────────────────────────────

export interface ClassAttendanceEntry {
  id: number;
  student_id: number;
  class_id: number;
  section_id: number;
  date: string;
  status: 'present' | 'absent' | 'late';
  note: string | null;
  marked_by: number;
  updated_by: number | null;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
}

export interface MarkClassAttendanceRequest {
  class_id: number;
  section_id: number;
  date: string;
  entries: { student_id: number; status: string; note?: string }[];
}

export interface MarkClassAttendanceResponse {
  message: string;
  data: null;
}

export interface UpdateClassAttendanceResponse {
  message: string;
  data: ClassAttendanceEntry;
}

// ─── Insights types ───────────────────────────────────────────────────────────

export interface MonthlySummaryMonth {
  month: string;
  month_date: string;
  present_pct: number | null;
  absent_pct: number | null;
  late_pct: number | null;
  present: number | null;
  absent: number | null;
  late: number | null;
  total_days: number | null;
}

export interface MonthlySummaryDay {
  date: string;
  status: 'present' | 'absent' | 'late' | null;
  note: string | null;
  photo: string | null;
  photo_url: string | null;
  marked_at_device: string | null;
  created_at: string | null;
  is_locked: boolean | null;
}

export interface TeacherMonthlySummaryResponse {
  teacher_id: number;
  teacher_name: string;
  academic_year: string;
  months: MonthlySummaryMonth[];
  days?: MonthlySummaryDay[];
}

export interface StudentMonthlySummaryResponse {
  class_id: number;
  class_name: string;
  section_id: number;
  section_name: string;
  academic_year: string;
  total_students: number;
  months: MonthlySummaryMonth[];
}

// ─── Student academic summary ─────────────────────────────────────────────────

export interface StudentAcademicSummaryResponse {
  student_id: number;
  student_uid: string;
  full_name: string;
  roll_number: number | null;
  month: string;
  present: number;
  absent: number;
  late: number;
  total_marked: number;
}

// ─── Teacher attendance APIs ──────────────────────────────────────────────────

export async function markTeacherAttendance(
  body: MarkTeacherAttendanceRequest,
): Promise<MarkTeacherAttendanceResponse> {
  return apiFetch<MarkTeacherAttendanceResponse>(API_URLS.MARK_ATTENDANCE_TEACHER, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getTeacherAttendanceList(params: {
  teacher_id: number;
  date?: string;
  status?: string;
}): Promise<TeacherAttendanceEntry[]> {
  const q = new URLSearchParams({ teacher_id: String(params.teacher_id) });
  if (params.date)   q.set('date',   params.date);
  if (params.status) q.set('status', params.status);

  const data = await apiFetch<TeacherAttendanceEntry[]>(
    `${API_URLS.GET_ATTENDANCE_TEACHER}?${q.toString()}`,
    { method: 'GET' },
  );
  return Array.isArray(data) ? data : [data];
}

// ─── Class / student attendance APIs ─────────────────────────────────────────

export async function markClassAttendance(
  body: MarkClassAttendanceRequest,
): Promise<MarkClassAttendanceResponse> {
  return apiFetch<MarkClassAttendanceResponse>(API_URLS.MARK_CLASS_ATTENDANCE, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getClassAttendance(params: {
  class_id: number;
  section_id: number;
  date?: string;
  student_id?: number;
}): Promise<ClassAttendanceEntry[]> {
  const q = new URLSearchParams({
    class_id:   String(params.class_id),
    section_id: String(params.section_id),
  });
  if (params.date)       q.set('date',       params.date);
  if (params.student_id) q.set('student_id', String(params.student_id));

  const data = await apiFetch<ClassAttendanceEntry[]>(
    `${API_URLS.GET_CLASS_ATTENDANCE}?${q.toString()}`,
    { method: 'GET' },
  );
  return Array.isArray(data) ? data : [];
}

export async function updateClassAttendance(
  student_id: number,
  date: string,
  body: { status: 'present' | 'absent' | 'late'; note?: string | null },
): Promise<UpdateClassAttendanceResponse> {
  return apiFetch<UpdateClassAttendanceResponse>(
    API_URLS.UPDATE_CLASS_ATTENDANCE(student_id, date),
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function getStudentAcademicSummary(
  student_id: number,
): Promise<StudentAcademicSummaryResponse> {
  return apiFetch<StudentAcademicSummaryResponse>(
    API_URLS.GET_STUDENT_ACADEMIC_SUMMARY(student_id),
    { method: 'GET' },
  );
}

// ─── Insights APIs ────────────────────────────────────────────────────────────

export async function getTeacherMonthlySummary(
  teacherId: number,
  academicYear: string,
  month?: number,
): Promise<TeacherMonthlySummaryResponse> {
  return apiFetch<TeacherMonthlySummaryResponse>(
    API_URLS.GET_TEACHER_DETAILS({
      teacher_id: teacherId,
      academic_year: academicYear,
      month,
    }),
    { method: 'GET' },
  );
}

export async function getStudentMonthlySummary(
  classId: number,
  sectionId: number,
  academicYear: string,
): Promise<StudentMonthlySummaryResponse> {
  return apiFetch<StudentMonthlySummaryResponse>(
    API_URLS.GET_STUDENT_DETAILS(classId, sectionId, academicYear),
    { method: 'GET' },
  );
}